import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const HeroContainer = styled.section`
  position: relative;
  height: 90vh;
  min-height: 600px;
  overflow: hidden;
  background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
  display: flex;
  align-items: center;
`;

export const SlideContainer = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  z-index: ${({ $active }) => ($active ? 2 : 1)};
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  /*
   * Clean crossfade without a white flash:
   * - the incoming (active) slide fades 0 -> 1 ON TOP (z-index 2)
   * - the outgoing slide holds at full opacity underneath and only drops to 0
   *   AFTER the fade-in completed (0s change delayed by the fade duration),
   *   so the white container background is never revealed mid-transition.
   */
  transition: ${({ $active }) =>
    $active
      ? 'opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
      : 'opacity 0s linear 1.2s'};
  display: grid;
  grid-template-columns: 45% 55%;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

export const SlideImage = styled.div<{ $image: string; $parallax: number }>`
  width: 100%;
  height: 100%;
  background-image: url(${({ $image }) => $image});
  background-size: cover;
  background-position: center;
  position: relative;
  clip-path: polygon(15% 0, 100% 0, 100% 100%, 0 100%);
  transform: translateY(${({ $parallax }) => $parallax * 0.15}px);
  transition: transform 0.1s ease-out;
  grid-column: 2;
  grid-row: 1;

  @media (max-width: 1024px) {
    clip-path: polygon(0 10%, 100% 0, 100% 100%, 0 90%);
    grid-column: 1;
    grid-row: 2;
    height: 60vh;
  }

  @media (max-width: 768px) {
    clip-path: polygon(0 5%, 100% 0, 100% 100%, 0 95%);
    height: 50vh;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.05) 0%,
      rgba(0, 0, 0, 0.1) 100%
    );
  }
`;

export const SlideContent = styled.div<{ $active?: boolean }>`
  grid-column: 1;
  grid-row: 1;
  padding: 0 5% 0 8%;
  z-index: 2;
  max-width: 600px;
  /* No opacity crossfade (that flashed white). Instead the text stays fully
     opaque and only moves: the outgoing slide is hidden instantly via
     visibility, the incoming slide glides in sideways from the left. */
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transform: translate3d(${({ $active }) => ($active ? '0' : '-60px')}, 0, 0);
  will-change: transform;
  backface-visibility: hidden;
  transition: ${({ $active }) =>
    $active
      ? 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.15s, visibility 0s'
      : 'transform 0s linear, visibility 0s'};

  @media (max-width: 1024px) {
    grid-row: 1;
    grid-column: 1;
    padding: 5%;
    text-align: center;
    max-width: none;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

export const SlideTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 300;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.1;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    letter-spacing: 0.02em;
  }
`;

export const SlideSubtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.3rem);
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2.5rem;
  line-height: 1.7;
  max-width: 480px;
  padding-left: 2.5rem;
  border-left: 3px solid ${({ theme }) => theme.colors.accent};
  animation: ${fadeIn} 1s ease-out 0.4s both;
  
  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
    padding-left: 2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 2rem;
    padding-left: 1.5rem;
    border-left-width: 2px;
  }
`;

export const SlideButton = styled.button`
  padding: 1rem 2.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.accent};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeIn} 1s ease-out 0.6s both;
  font-family: ${({ theme }) => theme.fonts.primary};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.dark};
    transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: -1;
  }

  &:hover::before {
    left: 0;
  }

  &:hover {
    color: #ffffff;
    border-color: ${({ theme }) => theme.colors.dark};
    box-shadow: 0 8px 24px rgba(101, 116, 85, 0.35);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 0.8rem;
  }
`;

export const DotsContainer = styled.div`
  position: absolute;
  bottom: 3rem;
  left: 8%;
  display: flex;
  gap: 1rem;
  z-index: 10;
  
  @media (max-width: 1024px) {
    left: 50%;
    transform: translateX(-50%);
  }
  
  @media (max-width: 768px) {
    bottom: 2rem;
    gap: 0.75rem;
  }
`;

export const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '50px' : '10px')};
  height: 10px;
  border-radius: 5px;
  background: ${({ $active, theme }) => 
    $active ? theme.colors.accent : 'rgba(101, 116, 85, 0.3)'};
  border: none;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    opacity: 0.8;
  }
`;
